import { put, take, fork, call, select, all } from "redux-saga/effects";
import { ClaimAction, ClaimScope } from "./types";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import * as Api from "../../service/api";
import { ScopeErrorAction } from "../scope-error/types";
import { cleanUpClaimInfo, createClaim, setClaimInfo } from "./actions";
import RouteNames from "../../routes/type";
import { navigateTo } from "../../helper";
import {
  MapApiDamage,
  getOptionValue,
  mapApiClaimItem,
  nextStepRedirect,
} from "./helpers";
import { RootState } from "../root-reducer";
import { mapApiFppPlan } from "../user/helpers";
import moment from "moment";
import { ClaimStatus } from "../../types/claim-status";
import { WarrantyItem } from "../../types/warranty-item";
import axios from "axios";
import toast from "react-hot-toast";

function* createClaimFlow() {
  while (true) {
    const { payload } = yield take(ClaimAction.CREATE_CLAIM);
    const { warranty_id, showDraftModal, showClaimInProcessModal } = payload;

    try {
      yield put(enableScopeLoading(ClaimScope));

      const plan = yield select((state) =>
        state.user.plans.find((item) => item.id === warranty_id)
      );
      const currentDateTime = moment().format("YYYY-MM-DD HH:mm:ss");
      const data = yield call(Api.fileClaim.insertClaim, {
        warrentyId: warranty_id,
        customerId: plan.customerId,
        damagedate: currentDateTime,
        claimdate: currentDateTime,
        allowSMS: 1,
      });
      const result = data.result;
      if (data?.code === "DRAFT_EXIST") {
        showDraftModal?.(data?.claimId);
      } else if (data?.message === "CLAIM_IN_PROGRESS") {
        showClaimInProcessModal?.(data?.claimSatusCode);
      } else {
        const claimId = result.claim_id;
        yield put(
          setClaimInfo({
            claimId,
            plan,
            status: ClaimStatus.DRAFT,
            warranty_items: plan?.warrantyItems || [],
            customerId: plan?.customerId || "",
          })
        );
        yield navigateTo(
          `/${RouteNames.CREATE_CLAIM}?step=2&claimId=${claimId}&warranty_id=${warranty_id}`
        );
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* deleteDraftAndCreateClaimFlow() {
  while (true) {
    const request = yield take(ClaimAction.DELETE_CLAIM_CREATE_NEW);
    yield put(enableScopeLoading(ClaimScope));
    const { warranty_id, claimId } = request.payload;
    try {
      yield put(cleanUpClaimInfo());
      yield call(Api.fileClaim.deleteClaim, claimId);
      yield put(createClaim({ warranty_id, showDraftModal: () => {} }));
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* insertClaimItemsFlow() {
  while (true) {
    const request = yield take(ClaimAction.INSERT_CLAIM_ITEMS);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const requestedData = request.payload;
      const resClaimItems = yield call(
        Api.fileClaim.insertClaimItems,
        requestedData
      );
      const plan = yield select((state: RootState) => state.claim.plan);
      const claim_items = yield select(
        (state: RootState) => state.claim.claim_items || []
      );
      const warrantyItems = plan?.warrantyItems || [];
      requestedData.claim_items ||= [];
      if (resClaimItems.length > 0) {
        yield put(
          setClaimInfo({
            claim_items: requestedData.claim_items.map((item, idx) => {
              const warrItem: WarrantyItem = warrantyItems.find(
                (subItem) => item.selectedItemId === subItem.id
              );
              const oldItemData =
                claim_items.find(
                  (subItem) => subItem.id === item.claimItemId
                ) || {};
              return {
                id: resClaimItems[idx]?.claimItemId,
                warrntyItemId: item.selectedItemId,
                type: warrItem.type,
                manufacturer: getOptionValue(
                  item.manuFacturer,
                  "manufacturers"
                ),
                material: getOptionValue(item.material, "materialTypes"),
                color: getOptionValue(item.color, "colors"),
                documents_and_photos: oldItemData?.documents_and_photos || [],
                damages: oldItemData?.damages || [],
              };
            }),
          })
        );
        nextStepRedirect(4);
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* removeClaimItemsFlow() {
  while (true) {
    const request = yield take(ClaimAction.REMOVE_CLAIM_ITEM);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const { id: claimItemId } = request.payload;
      yield call(Api.fileClaim.removeClaimItem, claimItemId);
      const data = yield select((state: RootState) => state.claim.claim_items);
      const mappeddata = data.filter((item) => item.id !== claimItemId);
      yield put(setClaimInfo({ claim_items: mappeddata }));
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* insertClaimItemDamagesFlow() {
  while (true) {
    const request = yield take(ClaimAction.INSERT_CLAIM_ITEM_DAMGES);
    yield put(enableScopeLoading(ClaimScope));

    try {
      const claimItemDamageData = request?.payload || {};
      const { damage_item = [] } = claimItemDamageData;
      const { multipleDamageIds = [] } = yield call(
        Api.fileClaim.insertClaimItemDamages,
        claimItemDamageData
      );

      const updatedDamages = damage_item.map((damage, idx) => ({
        id: multipleDamageIds[idx],
        selectedItemId: damage["selectedItemId"],
        damageCategory: getOptionValue(damage["damageType"], "damageTypes"),
        specificLocation: getOptionValue(
          damage["specificLocation"],
          "specificLocations"
        ),
        specificDamage: getOptionValue(
          damage["specificDamage"],
          "specificDamages"
        ),
        occuredReason: getOptionValue(damage["howDidOccur"], "howDidOccur"),
        actionTaken: getOptionValue(damage["actionTaken"], "actionsTaken"),
        damageDate: damage["damageDate"]
          ? moment(damage["damageDate"], "YYYY-MM-DD").toDate()
          : null,
        notes: damage["notes"],
      }));
      const claim_items = yield select(
        (state: RootState) => state.claim.claim_items || []
      );
      const updatedClaimItems = claim_items.map((item) => {
        const matchingDamages = updatedDamages.filter(
          (damage) => +damage.selectedItemId === +item.id
        );
        return {
          ...item,
          damages: matchingDamages,
        };
      });
      yield put(
        setClaimInfo({
          claim_items: updatedClaimItems,
        })
      );
      nextStepRedirect(5);
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* submitClaimFlow() {
  while (true) {
    yield take(ClaimAction.SUBMIT_CLAIM);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const claimId = yield select((state) => state.claim.claimId);
      const custId = yield select((state) => state.claim.plan.customerId);
      const data = yield call(Api.fileClaim.submitClaim, claimId, custId);
      if (data.message === "Succeed") {
        nextStepRedirect(6);
      } else {
        yield put(disableScopeLoading(ClaimScope));
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
      yield put(disableScopeLoading(ClaimScope));
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* claimItemUploadFlow() {
  while (true) {
    const request = yield take(ClaimAction.CLAIM_ITEM_FILE_UPLOAD);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const { files, claimItemId } = request.payload;
      const claimId = yield select((state) => state.claim.claimId);
      const claim_items = yield select(
        (state: RootState) => state.claim.claim_items
      );

      const fileNames = yield all(
        files.map(function* (item) {
          const { url = "" } = yield call(
            Api.fileClaim.getPreSignedUrl,
            claimId,
            item.type
          );
          yield axios.put(url, item, {
            headers: {
              "Content-Type": item.type,
            },
          });
          const data = new URL(url);
          data.search = "";
          const fileName = data.toString().split("/").pop();
          return fileName;
        })
      );

      if (fileNames.length > 0) {
        const res = yield call(Api.fileClaim.claimItemSaveFileInfo, {
          claimId: claimId,
          claimItemsId: claimItemId,
          docName: fileNames,
        });
        const claim_item_data = claim_items.find(
          ({ id }) => id === claimItemId
        );

        const oldNewCombined = [
          ...(claim_item_data.documents_and_photos || []),
          ...res.map(({ id, name }) => ({ id, fileName: name })),
        ];
        yield put(
          setClaimInfo({
            claim_items: claim_items.map((item) =>
              item.id === claim_item_data.id
                ? {
                    ...item,
                    documents_and_photos: oldNewCombined,
                  }
                : item
            ),
          })
        );

        toast.success("files uploaded sucessfully", {
          position: "bottom-center",
        });
      }
    } catch (error) {
      toast.error("file not uploaded.", {
        position: "bottom-center",
      });
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* fetchClaimInfoFlow() {
  while (true) {
    const request = yield take(ClaimAction.FETCH_CLAIM_INFO);
    yield put(enableScopeLoading(ClaimScope));

    try {
      const { claimId, warranty_id } = request.payload;

      let plan = {};
      let claim = {};
      let warranty_items = [];
      let mapped_claim_items = [];
      let damageItems = [];

      if (warranty_id) {
        const warrantyData = yield call(Api.warranty.getPlanById, warranty_id);
        const customerPlan = warrantyData.customers[0];
        plan = mapApiFppPlan(customerPlan);
        warranty_items = plan["warrantyItems"];
      }

      if (claimId) {
        const tempClaim = yield call(Api.fileClaim.getClaim, claimId);
        const {
          items: claim_items = [],
          damageAreasDisp: damages = [],
          documents = [],
        } = tempClaim || {};

        claim = tempClaim;

        mapped_claim_items = claim_items.map((item) => {
          const itemId = item.item_id;
          const mapped = mapApiClaimItem(item);
          const itemDamages = (damages || [])
            .filter((dmg) => dmg.item_id === itemId)
            .map(MapApiDamage);

          return {
            ...mapped,
            documents_and_photos: documents
              .filter((subitem) => +subitem.gbs_claim_items_id === mapped.id)
              .map(({ doc_name, id }) => ({ id, fileName: doc_name })),
            damages: itemDamages,
          };
        });
      }

      yield put(
        setClaimInfo({
          plan,
          claimId,
          warranty_items,
          claim_items: mapped_claim_items,
          data: claim,
          damage_items: damageItems,
        })
      );
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error: "CLAIM_NOT_FOUND",
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* claimItemDeleteImageFlow() {
  while (true) {
    const request = yield take(ClaimAction.CLAIM_ITEM_DELETE_DOCUMENT);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const { docId, claimItemId } = request.payload;
      const claim_items = yield select(
        (state: RootState) => state.claim.claim_items
      );
      const removeFile = yield call(Api.fileClaim.removeClaimPhoto, docId);
      const claim_item_data = claim_items.find(({ id }) => id === claimItemId);
      if (removeFile) {
        const removedFileData = claim_item_data?.documents_and_photos?.filter(
          (item) => item.id !== docId
        );

        yield put(
          setClaimInfo({
            claim_items: claim_items.map((item) =>
              item.id === claim_item_data.id
                ? {
                    ...item,
                    documents_and_photos: removedFileData,
                  }
                : item
            ),
          })
        );
        toast.success("file deleted sucessfully", {
          position: "bottom-center",
        });
      } else {
        toast.error("something went wrong, can't delete file.", {
          position: "bottom-center",
        });
      }
    } catch (error) {
      toast.error("file not deleted.", {
        position: "bottom-center",
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* deleteClaimDamageItemFlow() {
  while (true) {
    const request = yield take(ClaimAction.DELETE_DAMAGE_ITEM);
    yield put(enableScopeLoading(ClaimScope));

    try {
      const damageIdToDelete = request.payload;

      yield call(Api.fileClaim.deleteClaimDamageItem, damageIdToDelete);
      const claim_items = yield select(
        (state: RootState) => state.claim.claim_items
      );
      const updatedClaimItems = claim_items.map((item) => {
        const filteredDamages = (item.damages || []).filter(
          (damage) => damage.id !== damageIdToDelete
        );
        return {
          ...item,
          damages: filteredDamages,
        };
      });

      yield put(
        setClaimInfo({
          claim_items: updatedClaimItems,
        })
      );
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

export default function* userSaga() {
  yield fork(createClaimFlow);
  yield fork(fetchClaimInfoFlow);
  yield fork(deleteDraftAndCreateClaimFlow);
  yield fork(insertClaimItemDamagesFlow);
  yield fork(removeClaimItemsFlow);
  yield fork(insertClaimItemsFlow);
  yield fork(submitClaimFlow);
  yield fork(claimItemUploadFlow);
  yield fork(claimItemDeleteImageFlow);
  yield fork(deleteClaimDamageItemFlow);
}
